import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'next-i18next';
import Icon from '@components/icons/Icon';
import styles from '@components/QuantityInput/QuantityInput.styles';
import useInflectedTranslation from '@hooks/useInflectedTranslation';
type PropsType = {
  position?: string;
  qty: number;
  error?: boolean;
  handleChangeQty: (qty) => void;
  confirmUnavailableQuantity?: (position: string | null, deleteProduct?: boolean) => void;
  isDip?: boolean;
  multiplication?: string;
  disabled?: boolean;
  maxValue?: number;
};
const QuantityInput = ({
  position,
  qty,
  error,
  handleChangeQty,
  confirmUnavailableQuantity,
  isDip = false,
  multiplication = '0',
  disabled = false,
  maxValue = 999
}: PropsType) => {
  const [qtyValue, setQtyValue] = useState<number | ''>(qty);
  const {
    getInflectedTranslation
  } = useInflectedTranslation();
  const {
    t
  } = useTranslation('common');
  useEffect(() => {
    setQtyValue(qty);
  }, [qty]);
  const sendNewQuantity = useCallback((newQty: number) => {
    handleChangeQty(newQty);
  }, [handleChangeQty]);
  const debouncedSendNewQuantity = useMemo(() => debounce(sendNewQuantity, 500), [sendNewQuantity]);
  const handleQty = useCallback((newValue: number, min: number, max: number, userAction = true) => {
    if (confirmUnavailableQuantity && position) {
      if (+max && newValue > +max) confirmUnavailableQuantity(position);else if (userAction) confirmUnavailableQuantity(position, true);
    }
    const newQty = Math.max(Number(min), Math.min(Number(max), Number(newValue)));
    setQtyValue(newQty);
    if (newQty) {
      debouncedSendNewQuantity(newQty);
    }
  }, [confirmUnavailableQuantity, position, debouncedSendNewQuantity]);
  const handleChangeInputQty = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      value,
      min,
      max
    } = e.target;
    handleQty(Number(value.replace(/[^0-9]/, '')), +min, +max);
  };
  const isMinusDisabled = qtyValue !== '' && qtyValue <= 1 || disabled;
  const isPlusDisabled = qtyValue !== '' && qtyValue >= maxValue || disabled;
  const handleClickButtonQuantity = (isPlus?: boolean) => {
    if (isPlus ? isPlusDisabled : isMinusDisabled) return;
    if (qtyValue === '' || qtyValue <= 1 && !isPlus) return;
    const newQty = isPlus ? qtyValue + 1 : qtyValue - 1;
    if (newQty > maxValue && isPlus) {
      if (confirmUnavailableQuantity && position) confirmUnavailableQuantity(position);
      setQtyValue(maxValue);
      debouncedSendNewQuantity(maxValue);
    } else {
      if (confirmUnavailableQuantity && position) confirmUnavailableQuantity(position, true);
      setQtyValue(newQty);
      debouncedSendNewQuantity(newQty);
    }
  };
  useEffect(() => {
    handleQty(qty, 1, maxValue, false);
  }, [qty, maxValue, handleQty]);
  const quantityLabelId = `quantity-label-${position || 'default'}`;
  const quantityErrorId = `quantity-error-${position || 'default'}`;
  const quantityInputId = `quantity-input-${position || 'default'}`;
  return <div sx={styles.quantityInputContainer(isDip, error)}>
      <label className="visually-hidden" id={quantityLabelId} htmlFor={quantityInputId} title={t('cart.quantity')}>
        {t('cart.quantity')}
      </label>
      <div id={quantityErrorId} role="alert" className="visually-hidden" aria-live="polite">
        {error ? t('cart.quantityError') : ''}
      </div>
      <button type="button" onClick={() => handleClickButtonQuantity()} sx={styles.quantityButton(isMinusDisabled)} aria-disabled={isMinusDisabled} aria-label={t('cart.decreaseQuantity')} title={t('cart.decreaseQuantity')}>
        <Icon icon="Minus" size="24" title={t('cart.decreaseQuantity')} ariaHidden />
      </button>
      {isDip ? <div sx={styles.quantityDipInputBox}>
          <span sx={styles.quantityDipRow}>
            <input id={quantityInputId} type="number" name="quantity" onBlur={() => setQtyValue(qtyValue)} onChange={handleChangeInputQty} value={qtyValue} min="1" max={isDip ? 999 : maxValue} sx={styles.quantityInput()} disabled={disabled} aria-labelledby={quantityLabelId} aria-invalid={error ? 'true' : 'false'} aria-errormessage={error ? quantityErrorId : undefined} />
            <p className="subtitle"> {getInflectedTranslation(qtyValue || 1, 'pack')}</p>
          </span>
          {!!Number(multiplication) && <p className="caption">
              {' '}
              {Number(qtyValue !== '' ? Number(multiplication) * qtyValue : multiplication).toFixed(2)} m2
            </p>}
        </div> : <input id={quantityInputId} type="number" name="quantity" onBlur={() => setQtyValue(qty)} onChange={handleChangeInputQty} value={qtyValue} min="1" max={isDip ? 999 : maxValue} sx={styles.quantityInput()} disabled={disabled} aria-labelledby={quantityLabelId} aria-invalid={error ? 'true' : 'false'} aria-errormessage={error ? quantityErrorId : undefined} />}
      <button type="button" onClick={() => handleClickButtonQuantity(true)} sx={styles.quantityButton(isPlusDisabled)} aria-disabled={isPlusDisabled} aria-label={t('cart.increaseQuantity')} title={t('cart.increaseQuantity')}>
        <Icon icon="Add" size="24" title={t('cart.increaseQuantity')} ariaHidden />
      </button>
    </div>;
};
export default QuantityInput;